import AddIcon from '@mui/icons-material/Add';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import RefreshIcon from '@mui/icons-material/Refresh';
import { LoadingButton } from '@mui/lab';
import { Button, Chip, InputAdornment, Stack, TextField } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid-premium';
import { useFormik } from 'formik';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import { useEffect, useMemo, useRef, useState } from 'react';
import { AsyncDialog, AsyncDialogRef } from '../../../../components/AsyncDialog/AsyncDialog';
import {
  ConfirmationDialog,
  ConfirmationDialogData,
} from '../../../../components/ConfirmationDialog/ConfirmationDialog';
import { SectionCard, TableCard } from '../../../../components/Section';
import { RtkQueryTable, RtkQueryTableModels } from '../../../../components/Table/RtkQueryTable';
import {
  TREASURY_CONTRIBUTION_PENDING,
  TREASURY_CONTRIBUTION_STATUS_COLORS,
  TREASURY_FIRST_PAGE,
  TREASURY_GRID_HEIGHT,
  TREASURY_LIST_PAGE_SIZE,
} from '../../../../constants/treasury';
import { treasuryContributionFormFields } from '../../../../forms/fields/formFields/treasuryContribution';
import { treasuryContributionSchema } from '../../../../forms/validationSchema/formSchema/treasuryContribution';
import { usePermissions } from '../../../../hooks';
import {
  useCreateTreasuryContributionMutation,
  useExecuteTreasuryContributionMutation,
  useFetchTreasuryContributionsQuery,
} from '../../../../redux/features/treasury/treasury.apiSlice';
import { TreasuryContribution } from '../../../../types/treasury';
import * as permissions from '../../../../utils/permissions';
import { getTreasuryErrorMessage } from '../../../../utils/treasury';

type TreasuryContributionsTabProps = {
  companyId: number;
};

const formatDateTime = ({ value }: { value?: string }) => (value ? moment(value).format('MM/DD/YYYY HH:mm') : '');

/**
 * Contributions — the company puts money in. Create adds a PENDING contribution (100% company, no monthly limit);
 * Execute debits the company VIRTUAL_TREASURY bank and starts the buy. thriftplan refuses both unless the company
 * Alpaca account is ACTIVE.
 */
export const TreasuryContributionsTab = ({ companyId }: TreasuryContributionsTabProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const canCreate = usePermissions({ items: [permissions.Treasury.createContribution] });
  const canExecute = usePermissions({ items: [permissions.Treasury.executeContribution] });
  const confirmRef = useRef<AsyncDialogRef<ConfirmationDialogData, boolean>>(null);
  const [tableModels, setTableModels] = useState<RtkQueryTableModels>({ paginationModel: TREASURY_FIRST_PAGE });
  // The grid keeps its page itself; a new key opens it again on the first page.
  const [tableKey, setTableKey] = useState(0);
  const { page } = tableModels.paginationModel;
  // Rows are cached per page, so the grid only ever shows the page it is on.
  const { data, isFetching, isError, error, refetch } = useFetchTreasuryContributionsQuery({
    companyId,
    page: page + 1,
    limit: TREASURY_LIST_PAGE_SIZE,
  });
  const [createContribution, { isLoading: isCreating }] = useCreateTreasuryContributionMutation();
  const [executeContribution] = useExecuteTreasuryContributionMutation();

  useEffect(() => {
    if (isError) {
      enqueueSnackbar(getTreasuryErrorMessage(error, 'Failed to load the contributions'), { variant: 'error' });
    }
  }, [isError, error, enqueueSnackbar]);

  // `dirty` too: resetForm after a create clears the errors without validating the empty amount again.
  const { handleSubmit, values, handleChange, handleBlur, touched, errors, isValid, dirty, resetForm } = useFormik({
    initialValues: { [treasuryContributionFormFields.amount]: '' },
    validationSchema: treasuryContributionSchema,
    validateOnMount: true,
    onSubmit: async (formValues) => {
      try {
        await createContribution({
          companyId,
          amount: Number(formValues[treasuryContributionFormFields.amount]),
        }).unwrap();
        enqueueSnackbar('Contribution created — execute it to start the buy.', { variant: 'success' });
        resetForm();

        // The new row is on the first page. On it already, the create refetched it.
        if (page !== 0) {
          setTableModels({ paginationModel: TREASURY_FIRST_PAGE });
          setTableKey((key) => key + 1);
        }
      } catch (createError) {
        enqueueSnackbar(getTreasuryErrorMessage(createError, 'Failed to create the contribution'), {
          variant: 'error',
        });
      }
    },
  });

  const columns = useMemo<GridColDef<TreasuryContribution>[]>(() => {
    const execute = (contribution: TreasuryContribution) =>
      confirmRef.current?.show({
        title: 'Execute Contribution',
        description: `Debit SAR ${contribution.totalAmountToPay} from the company treasury bank and start the buy? Treasury orders can't be cancelled.`,
        acceptButtonText: 'Execute',
        rejectButtonText: 'Cancel',
        confirmButtonColor: 'primary',
        onConfirm: async () => {
          try {
            await executeContribution({ companyId, contributionId: contribution.id }).unwrap();
            enqueueSnackbar('Contribution execution started', { variant: 'success' });
          } catch (executeError) {
            enqueueSnackbar(getTreasuryErrorMessage(executeError, 'Failed to execute the contribution'), {
              variant: 'error',
            });
          }
        },
      });

    return [
      { field: 'id', headerName: 'ID', width: 90, sortable: false },
      { field: 'totalAmountToPay', headerName: 'Amount (SAR)', width: 160, sortable: false },
      {
        field: 'status',
        headerName: 'Status',
        width: 140,
        sortable: false,
        renderCell: ({ row }) => (
          <Chip size="small" label={row.status} color={TREASURY_CONTRIBUTION_STATUS_COLORS[row.status] ?? 'default'} />
        ),
      },
      { field: 'createdAt', headerName: 'Created', width: 170, sortable: false, valueFormatter: formatDateTime },
      { field: 'updatedAt', headerName: 'Updated', width: 170, sortable: false, valueFormatter: formatDateTime },
      {
        field: 'actions',
        headerName: 'Action',
        width: 130,
        sortable: false,
        renderCell: ({ row }) =>
          row.status === TREASURY_CONTRIBUTION_PENDING && (
            <Button size="small" startIcon={<PlayArrowIcon />} disabled={!canExecute} onClick={() => execute(row)}>
              Execute
            </Button>
          ),
      },
    ];
  }, [companyId, canExecute, executeContribution, enqueueSnackbar]);

  const amountError = touched[treasuryContributionFormFields.amount] && errors[treasuryContributionFormFields.amount];

  return (
    <Stack spacing={2}>
      <SectionCard title="New Contribution" description="100% company money. Any number per month.">
        <form onSubmit={handleSubmit}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'flex-start' }}>
            <TextField
              id={treasuryContributionFormFields.amount}
              name={treasuryContributionFormFields.amount}
              label="Amount"
              type="number"
              size="small"
              value={values[treasuryContributionFormFields.amount]}
              onChange={handleChange}
              onBlur={handleBlur}
              error={Boolean(amountError)}
              helperText={amountError || ' '}
              InputProps={{ startAdornment: <InputAdornment position="start">SAR</InputAdornment> }}
              sx={{ minWidth: 240 }}
            />
            <LoadingButton
              type="submit"
              variant="contained"
              startIcon={<AddIcon />}
              loading={isCreating}
              disabled={!isValid || !dirty || !canCreate}
            >
              Create Contribution
            </LoadingButton>
          </Stack>
        </form>
      </SectionCard>

      <TableCard
        title="Contributions"
        description="Only a pending contribution can be executed."
        action={
          <Button size="small" startIcon={<RefreshIcon />} onClick={refetch}>
            Refresh
          </Button>
        }
      >
        <RtkQueryTable
          key={tableKey}
          models={tableModels}
          onModelsChange={setTableModels}
          rows={data?.contributions ?? []}
          rowCount={data?.total ?? 0}
          columns={columns}
          loading={isFetching}
          pageSizeOptions={[TREASURY_LIST_PAGE_SIZE]}
          disableColumnFilter
          wrapperHeight={TREASURY_GRID_HEIGHT}
        />
      </TableCard>
      <AsyncDialog ref={confirmRef} dialog={ConfirmationDialog} />
    </Stack>
  );
};
